import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { InvoiceDisplayStatusV2 } from 'anchor-scout-common/types';
import { Box, Column, Divider, Spinner, TableListHeader, TableListProvider, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { getAccountingStatusFilterOptions, getInvoiceStatusFilterOptions, getIssuesPreventingPaymentFilterOptions, getProcessedByFilterOptions, mergePaymentDetailsFilter } from '~/components/InvoicesTable/InvoicesTable.utils';
import { FilterType as InvoiceFilterType, getActiveFilterGroups, getTableFilterConfig, getTableFilterLabels, invoiceFilterTextsPath } from '~/components/InvoicesTable/invoicesTableFilters';
import TableFilters from '~/components/TableFilters/TableFilters';
import { isInitialFilterValue, seedNonRemovableFilterValues } from '~/components/TableFilters/TableFilters.utils';
import { useStore } from '~/contexts';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useLocaleKeys } from '~/i18n';
import { useIoContext } from '~/providers/IoContext';
import { selectors } from '~/store/state-selectors';
import styles from './ClientInvoicesListView.module.scss';
import { applyClientInvoicesFilters, applyClientInvoicesSort, DEFAULT_SORT, distinctAgreementCount, EMPTY_FILTERS, getClientInvoicesFilterCounters, getClientInvoicesTableDefs, parseDateRangeFilter } from './ClientInvoicesListView.utils';
import { ClientInvoicesListViewRowOld } from './ClientInvoicesListViewRowOld';
const clientInvoicesListViewTextsPath = 'client.detail.tabContent.issuedInvoices';
export const GetClientInvoicesListViewOldTID = (parentTestId)=>getTestIds(parentTestId);
export function ClientInvoicesListViewOld(param) {
    let { invoices, isLoading, customerId, testId = '', hideManagePaymentMethods = false } = param;
    const { ClientInvoicesListViewOldTestIds: TID } = useMemo(()=>GetClientInvoicesListViewOldTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys(clientInvoicesListViewTextsPath);
    const { t: invoiceTexts } = useLocaleKeys(invoiceFilterTextsPath);
    const isMobileDevice = useIsMobileForHomePage();
    const { getNow } = useIoContext();
    const { urlState, changeUrlState } = useStateFromUrl({
        status: {
            defaultValue: '',
            applyDefaultOnlyOnMount: true
        }
    });
    const initialStatusFromUrl = useMemo(()=>{
        const raw = urlState.status;
        if (!raw) return [];
        return raw.split(',').map((s)=>s.trim()).filter((s)=>Object.values(InvoiceDisplayStatusV2).includes(s));
    }, [
        urlState.status
    ]);
    const [search, setSearch] = useState('');
    const [selectedFilters, setSelectedFilters] = useState(()=>initialStatusFromUrl.length > 0 ? {
            [InvoiceFilterType.InvoiceStatus]: initialStatusFromUrl
        } : {});
    const [sort, setSort] = useState(DEFAULT_SORT);
    const seedStripped = useRef(false);
    useEffect(()=>{
        if (seedStripped.current) return;
        seedStripped.current = true;
        if (urlState.status) changeUrlState({
            status: ''
        });
    }, [
        urlState.status
    ]);
    const showAgreementName = useMemo(()=>distinctAgreementCount(invoices) > 1, [
        invoices
    ]);
    const { columnDefs, headerItems } = useMemo(()=>getClientInvoicesTableDefs({
            showAgreementName,
            texts: {
                invoiceId: texts.columns.invoiceId(),
                agreementName: texts.columns.agreementName(),
                invoiceStatus: texts.columns.invoiceStatus(),
                additionalInfo: texts.columns.additionalInfo(),
                issueDate: texts.columns.issueDate(),
                dueDate: texts.columns.dueDate(),
                total: texts.columns.total()
            }
        }), [
        showAgreementName
    ]);
    const sortIds = useMemo(()=>headerItems.map((h)=>h.id), [
        headerItems
    ]);
    const filterLabels = useMemo(()=>getTableFilterLabels(invoiceTexts.filterLabels), []);
    const isVendor = useStore(selectors.user.isVendor);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { filterConfig, activeFilterGroups } = useMemo(()=>{
        const counters = getClientInvoicesFilterCounters(invoices);
        const qboStatusFilterOptions = getAccountingStatusFilterOptions({
            counters: counters.qbo,
            texts: invoiceTexts.accountingStatusLabels
        });
        const xeroStatusFilterOptions = getAccountingStatusFilterOptions({
            counters: counters.xero,
            texts: invoiceTexts.accountingStatusLabels
        });
        return {
            filterConfig: getTableFilterConfig({
                invoiceStatusFilterOptions: getInvoiceStatusFilterOptions({
                    counters: counters.status,
                    texts: invoiceTexts.invoiceDisplayStatusLabels
                }),
                issuesPreventingPaymentFilterOptions: getIssuesPreventingPaymentFilterOptions({
                    counters: counters.paymentDetails,
                    texts: invoiceTexts.paymentDetailLabels
                }),
                processedByFilterOptions: getProcessedByFilterOptions({
                    counters: counters.paymentDetails,
                    texts: invoiceTexts.processedByLabels
                }),
                qboStatusFilterOptions,
                xeroStatusFilterOptions
            }),
            activeFilterGroups: getActiveFilterGroups({
                showQboIntegration,
                showXeroIntegration,
                hasQboFilterOptions: qboStatusFilterOptions.length > 0,
                hasXeroFilterOptions: xeroStatusFilterOptions.length > 0,
                filterGroupLabels: invoiceTexts.filterGroupLabels,
                isVendor,
                hideDateChip: false
            })
        };
    }, [
        invoices,
        isVendor,
        showQboIntegration,
        showXeroIntegration
    ]);
    const displayedFilters = useMemo(()=>_object_spread({}, seedNonRemovableFilterValues(activeFilterGroups, filterConfig), selectedFilters), [
        activeFilterGroups,
        filterConfig,
        selectedFilters
    ]);
    const hasActiveFilters = search !== '' || Object.entries(displayedFilters).some((param)=>{
        let [filter, value] = param;
        return !isInitialFilterValue(value, filterConfig[filter]);
    });
    const visible = useMemo(()=>{
        var _displayedFilters_InvoiceFilterType_InvoiceStatus, _displayedFilters_InvoiceFilterType_IssuesPreventingPayment, _displayedFilters_InvoiceFilterType_ProcessedBy, _displayedFilters_InvoiceFilterType_QboSync, _displayedFilters_InvoiceFilterType_XeroSync;
        const statuses = (_displayedFilters_InvoiceFilterType_InvoiceStatus = displayedFilters[InvoiceFilterType.InvoiceStatus]) !== null && _displayedFilters_InvoiceFilterType_InvoiceStatus !== void 0 ? _displayedFilters_InvoiceFilterType_InvoiceStatus : [];
        const { since, until } = parseDateRangeFilter(displayedFilters[InvoiceFilterType.Date], getNow());
        const filtered = applyClientInvoicesFilters(invoices, _object_spread_props(_object_spread({}, EMPTY_FILTERS), {
            search,
            statuses,
            issuedOnSince: since,
            issuedOnUntil: until,
            paymentDetails: mergePaymentDetailsFilter({
                issuesPreventingPayment: (_displayedFilters_InvoiceFilterType_IssuesPreventingPayment = displayedFilters[InvoiceFilterType.IssuesPreventingPayment]) !== null && _displayedFilters_InvoiceFilterType_IssuesPreventingPayment !== void 0 ? _displayedFilters_InvoiceFilterType_IssuesPreventingPayment : [],
                processedBy: (_displayedFilters_InvoiceFilterType_ProcessedBy = displayedFilters[InvoiceFilterType.ProcessedBy]) !== null && _displayedFilters_InvoiceFilterType_ProcessedBy !== void 0 ? _displayedFilters_InvoiceFilterType_ProcessedBy : []
            }),
            qbo: (_displayedFilters_InvoiceFilterType_QboSync = displayedFilters[InvoiceFilterType.QboSync]) !== null && _displayedFilters_InvoiceFilterType_QboSync !== void 0 ? _displayedFilters_InvoiceFilterType_QboSync : AccountingStatusFilter.All,
            xero: (_displayedFilters_InvoiceFilterType_XeroSync = displayedFilters[InvoiceFilterType.XeroSync]) !== null && _displayedFilters_InvoiceFilterType_XeroSync !== void 0 ? _displayedFilters_InvoiceFilterType_XeroSync : AccountingStatusFilter.All
        }));
        return applyClientInvoicesSort(filtered, sort, sortIds);
    }, [
        invoices,
        search,
        sort,
        sortIds,
        displayedFilters
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: TID.Root(),
            children: /*#__PURE__*/ _jsx(Spinner, {
                testId: TID.Loader()
            })
        });
    }
    if (invoices.length === 0) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: TID.Root(),
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: TID.Empty(),
                skin: "standard",
                children: texts.emptyMessage()
            })
        });
    }
    return /*#__PURE__*/ _jsx(Column, {
        testId: TID.Root(),
        gap: 16,
        className: styles.root,
        children: /*#__PURE__*/ _jsxs(TableListProvider, {
            hasActionColumn: true,
            columnDefs: columnDefs,
            sort: sort,
            onSortChange: setSort,
            variant: "minimal",
            children: [
                /*#__PURE__*/ _jsx(TableFilters, {
                    testIdPrefix: TID.Root(),
                    filterLabels: filterLabels,
                    filterConfig: filterConfig,
                    selectedFilters: displayedFilters,
                    setSelectedFilters: (next)=>{
                        setSelectedFilters(next);
                        if (urlState.status) changeUrlState({
                            status: ''
                        });
                    },
                    filterGroups: activeFilterGroups,
                    onRefresh: ()=>undefined,
                    onClear: hasActiveFilters ? ()=>{
                        setSearch('');
                        setSelectedFilters({});
                        if (urlState.status) changeUrlState({
                            status: ''
                        });
                    } : undefined,
                    search: {
                        bi: {
                            name: 'client_invoices_search'
                        },
                        value: search,
                        placeholder: texts.searchPlaceholder(),
                        testId: TID.Search(),
                        onChange: (event)=>setSearch(event.target.value),
                        onClear: ()=>setSearch('')
                    }
                }),
                /*#__PURE__*/ _jsxs(Box, {
                    className: cn(styles.tableBody, {
                        [styles.tableBodyDesktop]: !isMobileDevice
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(TableListHeader, {
                            testId: TID.Header(),
                            items: headerItems
                        }),
                        visible.length === 0 ? /*#__PURE__*/ _jsx(Box, {
                            children: /*#__PURE__*/ _jsx(Typography, {
                                testId: TID.NoResults(),
                                skin: "secondary",
                                children: texts.noResults()
                            })
                        }) : visible.map((invoice, idx)=>/*#__PURE__*/ _jsxs(Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(ClientInvoicesListViewRowOld, {
                                        invoice: invoice,
                                        customerId: customerId,
                                        showAgreementName: showAgreementName,
                                        testId: TID.Row(invoice.id),
                                        compact: isMobileDevice,
                                        disableMarginBottom: true,
                                        hideManagePaymentMethods: hideManagePaymentMethods
                                    }),
                                    !isMobileDevice && idx < visible.length - 1 && /*#__PURE__*/ _jsx(Divider, {
                                        margin: 0
                                    })
                                ]
                            }, invoice.id))
                    ]
                })
            ]
        })
    });
}
